import React from 'react'
import { v4 as uuidv4 } from 'uuid'
import styled from '@emotion/styled'
import { LabelPosition, CORE_PRIMARY_4, CORE_PRIMARY_6, FontWeight, LabelAlignment } from 'view/styles/constants'
import { InputProps } from './BaseInput'
import { getIssuesForField } from 'model'
import { flatMap } from 'lodash-es'

/*
    Field
    A Higher-Order component which is used to turn inputs into fields with a label
    Controls which can receive input should conform to the InputProps interface
    ie. provide an 'onUpdate' event which can be attached to.
*/
export interface FieldProps extends InputProps {
  label: string | JSX.Element
  labelPosition?: LabelPosition
  labelAlignment?: LabelAlignment
  fieldName?: string
  validationField?: string | string[]
  underline?: string | null
  onUpdate: (value: any, fieldName?: any, rawValue?: any) => void
  dataTestIdField?: string
}

interface FieldOptions {
  defaultLabelPosition?: LabelPosition
}

const flexDirections = {
  [LabelPosition.Left]: 'row',
  [LabelPosition.Right]: 'row-reverse',
  [LabelPosition.Top]: 'column',
  [LabelPosition.Bottom]: 'column-reverse'
}

const justifyContents = {
  [LabelPosition.Left]: 'flex-start',
  [LabelPosition.Right]: 'flex-end',
  [LabelPosition.Top]: 'flex-start',
  [LabelPosition.Bottom]: 'flex-end'
}

const alignItems = {
  [LabelPosition.Left]: 'center',
  [LabelPosition.Right]: 'center',
  [LabelPosition.Top]: 'flex-start',
  [LabelPosition.Bottom]: 'flex-start'
}

const textAlign = {
  [LabelAlignment.Left]: 'left',
  [LabelAlignment.Right]: 'right',
  [LabelAlignment.Center]: 'center'
}

const margins = {
  [LabelPosition.Left]: '0 10px 0 0',
  [LabelPosition.Right]: '0 0 0 6px',
  [LabelPosition.Top]: '0 0 1px 0',
  [LabelPosition.Bottom]: '1px 0 0 0'
}

export const FieldContainer = styled.div<{ labelPosition: LabelPosition; underline?: string }>`
  display: flex;
  flex-direction: ${({ labelPosition }) => flexDirections[labelPosition]};
  justify-content: ${({ labelPosition }) => justifyContents[labelPosition]};
  align-items: ${({ labelPosition }) => alignItems[labelPosition]};
  padding-bottom: ${({ underline }) => (underline ? '5px' : undefined)};
  border-bottom: ${({ underline }) => (underline ? `3px solid ${underline}` : undefined)};
  margin-bottom: ${({ underline }) => (underline ? '-8px' : undefined)};

  .field-input {
    width: 100%;
  }
`

const FieldLabel = styled.label<{ labelPosition: LabelPosition; labelAlignment?: LabelAlignment; disabled?: boolean }>`
  color: ${props => (props.disabled ? CORE_PRIMARY_4 : CORE_PRIMARY_6)};
  overflow: hidden;
  white-space: nowrap;
  font-size: 11px;
  font-weight: ${FontWeight.SemiBold};
  margin: ${({ labelPosition }) => margins[labelPosition]};
  text-align: ${({ labelAlignment }) => (labelAlignment ? textAlign[labelAlignment] : 'left')};
  flex: 0 0 auto;
`

export function Field<T extends InputProps>(Input: React.ComponentType<T>, options: FieldOptions = {}) {
  return class extends React.PureComponent<T & FieldProps> {
    private _id: string

    constructor(props: T & FieldProps) {
      super(props)
      /* use a genuinely unique id to prevent chrome from trying to autofill it with previous results */
      this._id = `field-${uuidv4()}`
    }

    render() {
      const {
        id,
        className,
        label,
        labelPosition,
        labelAlignment,
        onUpdate,
        fieldName,
        validationField,
        validationIssues,
        underline,
        dataTestIdField,
        ...remaining
      } = this.props as any
      const actualLabelPosition = labelPosition || options.defaultLabelPosition || LabelPosition.Top
      const inputId = id || this._id

      return (
        <FieldContainer data-test-id={dataTestIdField} className={className} labelPosition={actualLabelPosition} underline={underline}>
          <FieldLabel htmlFor={inputId} labelPosition={actualLabelPosition} labelAlignment={labelAlignment} disabled={this.props.disabled}>
            {label}
          </FieldLabel>
          <Input
            {...remaining}
            id={inputId}
            className="field-input"
            validationIssues={this.getFieldValidationIssues()}
            onUpdate={fieldName ? this.handleUpdate : onUpdate}
          />
        </FieldContainer>
      )
    }

    private handleUpdate = (value: any, rawValue?: any) => {
      const { onUpdate, fieldName } = this.props
      onUpdate(value, fieldName, rawValue)
    }

    private getFieldValidationIssues = () => {
      const { validationField, fieldName, validationIssues } = this.props as FieldProps
      if (Array.isArray(validationField)) {
        return flatMap(validationField, field => getIssuesForField(validationIssues || [], field))
      }
      const validationFieldName = validationField || fieldName || ''
      return getIssuesForField(validationIssues || [], validationFieldName)
    }
  }
}
